/**
 * ChartWidget - Display various chart types for metrics visualization
 *
 * Supports: Sparkline/Line, Area, Bar, Pie, Donut charts
 * All charts are custom SVG — no chart.js dependency for these widget-level charts.
 */

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, alpha } from "@mui/material";
import KDTypography from "components/KDTypography";
import BaseWidget from "./BaseWidget";
import { useMaterialUIController } from "context";
import { useDashboard } from "../context/DashboardContext";
import { getWidgetById, WIDGET_TYPES, dataTransformers } from "../config/widgetRegistry";

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  const keys = path.split(".");
  let current = obj;
  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    current = current[key];
  }
  return current;
};

const formatDateLabel = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
};

// ==================== Sparkline ====================
function Sparkline({ data, color, height = 60, showAxis = true, darkMode = true }) {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>
          No data available
        </KDTypography>
      </Box>
    );
  }

  const values = data.map((d) => (typeof d === "object" ? d.value || d.y || d.count || 0 : d));
  const labels = data.map((d) => (typeof d === "object" ? d.date || d.label || d.x : null));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const hasLabels = labels.length > 0 && labels[0];
  const axisHeight = showAxis && hasLabels ? 18 : 0;
  const chartHeight = height - axisHeight;

  const normalizedPoints = values.map((v, i) => ({
    x: (i / (values.length - 1 || 1)) * 100,
    y: 100 - ((v - min) / range) * 100,
  }));

  const linePath = normalizedPoints.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(" ");
  const areaPath = `${linePath} L 100 100 L 0 100 Z`;

  const axisLabels = [];
  if (hasLabels) {
    axisLabels.push({ label: formatDateLabel(labels[0]) });
    if (labels.length > 2) {
      axisLabels.push({ label: formatDateLabel(labels[Math.floor(labels.length / 2)]) });
    }
    axisLabels.push({ label: formatDateLabel(labels[labels.length - 1]) });
  }

  return (
    <Box sx={{ width: "100%", height, position: "relative", display: "flex", flexDirection: "column" }}>
      <Box sx={{ height: chartHeight, position: "relative" }}>
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: "visible" }}>
          <defs>
            <linearGradient id={`gradient-${color.replace("#", "")}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={color} stopOpacity="0.4" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#gradient-${color.replace("#", "")})`} />
          <path d={linePath} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
          <circle
            cx={normalizedPoints[normalizedPoints.length - 1]?.x}
            cy={normalizedPoints[normalizedPoints.length - 1]?.y}
            r="3"
            fill={color}
            style={{ filter: `drop-shadow(0 0 4px ${color})` }}
          />
        </svg>
      </Box>
      {showAxis && axisLabels.length > 0 && (
        <Box sx={{ height: axisHeight, display: "flex", justifyContent: "space-between", alignItems: "center", px: 0.5 }}>
          {axisLabels.map((item, i) => (
            <KDTypography
              key={i}
              variant="caption"
              sx={{
                color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                fontSize: "0.6rem",
                textAlign: i === 0 ? "left" : i === axisLabels.length - 1 ? "right" : "center",
              }}
            >
              {item.label}
            </KDTypography>
          ))}
        </Box>
      )}
    </Box>
  );
}

Sparkline.propTypes = { data: PropTypes.array, color: PropTypes.string, height: PropTypes.number, showAxis: PropTypes.bool };

// ==================== BarChart ====================
function BarChart({ data, color, height = 120, maxBars = 8, darkMode = true }) {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>No data available</KDTypography>
      </Box>
    );
  }

  const items = data.slice(0, maxBars);
  const values = items.map((d) => d.value || d.count || d.y || 0);
  const max = Math.max(...values) || 1;

  return (
    <Box sx={{ height, display: "flex", flexDirection: "column", gap: 1, overflow: "auto" }}>
      {items.map((item, index) => {
        const value = item.value || item.count || item.y || 0;
        const percentage = (value / max) * 100;
        const label = item.label || item.name || item.key || `Item ${index + 1}`;

        return (
          <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <KDTypography
              variant="caption"
              sx={{ color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)", minWidth: 80, fontSize: "0.7rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {label}
            </KDTypography>
            <Box sx={{ flex: 1, position: "relative", height: 16 }}>
              <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: "100%", bgcolor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", borderRadius: "4px" }} />
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${percentage}%`,
                  background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.6)} 100%)`,
                  borderRadius: "4px",
                  transition: "width 0.5s ease",
                }}
              />
            </Box>
            <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)", fontWeight: 600, fontSize: "0.7rem", minWidth: 40, textAlign: "right" }}>
              {value.toLocaleString()}
            </KDTypography>
          </Box>
        );
      })}
    </Box>
  );
}

BarChart.propTypes = { data: PropTypes.array, color: PropTypes.string, height: PropTypes.number, maxBars: PropTypes.number };

// ==================== DonutChart ====================
function DonutChart({ data, colors, size = 120, donut = true, darkMode = true }) {
  const defaultColors = ["#7C3AED", "#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#06B6D4", "#EF4444", "#8B5CF6"];

  if (!data || data.length === 0) {
    return (
      <Box sx={{ height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>No data available</KDTypography>
      </Box>
    );
  }

  const total = data.reduce((sum, d) => sum + (d.value || d.count || 0), 0);
  const colorPalette = colors || defaultColors;

  let currentAngle = -90;
  const segments = data.map((item, index) => {
    const value = item.value || item.count || 0;
    const percentage = (value / total) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    currentAngle += angle;
    return { ...item, value, percentage, startAngle, endAngle: currentAngle, color: colorPalette[index % colorPalette.length] };
  });

  const polarToCartesian = (cx, cy, r, deg) => {
    const rad = ((deg - 90) * Math.PI) / 180.0;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const describeArc = (x, y, radius, start, end) => {
    const s = polarToCartesian(x, y, radius, end);
    const e = polarToCartesian(x, y, radius, start);
    const large = end - start <= 180 ? "0" : "1";
    return `M ${s.x} ${s.y} A ${radius} ${radius} 0 ${large} 0 ${e.x} ${e.y}`;
  };

  const center = size / 2;
  const radius = size / 2 - 2;
  const innerRadius = donut ? radius * 0.6 : 0;

  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((seg, i) => {
          if (seg.percentage < 0.5) return null;
          return (
            <path
              key={i}
              d={describeArc(center, center, radius, seg.startAngle, seg.endAngle)}
              fill="none"
              stroke={seg.color}
              strokeWidth={radius - innerRadius}
              style={{ filter: `drop-shadow(0 2px 4px ${alpha(seg.color, 0.3)})` }}
            />
          );
        })}
        {donut && <circle cx={center} cy={center} r={innerRadius} fill={darkMode ? "rgba(26, 26, 46, 0.9)" : "rgba(255, 255, 255, 0.95)"} />}
        {donut && (
          <text x={center} y={center} textAnchor="middle" dominantBaseline="middle" fill={darkMode ? "white" : "rgba(0,0,0,0.87)"} fontSize="12" fontWeight="600">
            {total.toLocaleString()}
          </text>
        )}
      </svg>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5, overflow: "auto" }}>
        {segments.slice(0, 5).map((seg, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: seg.color, flexShrink: 0 }} />
            <KDTypography
              variant="caption"
              sx={{ color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)", fontSize: "0.65rem", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {seg.label || seg.name}
            </KDTypography>
            <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)", fontWeight: 600, fontSize: "0.65rem" }}>
              {seg.percentage.toFixed(0)}%
            </KDTypography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

DonutChart.propTypes = { data: PropTypes.array, colors: PropTypes.array, size: PropTypes.number, donut: PropTypes.bool };

// ==================== Main ChartWidget ====================
function ChartWidget({ widgetId, data: propData, loading: propLoading, error: propError, isExpanded = false }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { metricsData } = useDashboard();
  const widget = getWidgetById(widgetId);

  const categoryData = widget ? metricsData[widget.category] : null;
  const rawData = widget
    ? (propData ?? getNestedValue(categoryData, widget.dataKey) ?? getNestedValue(categoryData?.data, widget.dataKey))
    : null;

  const chartData = useMemo(() => {
    if (!widget || rawData === null) return null;
    if (widget.transform && dataTransformers[widget.transform]) {
      return dataTransformers[widget.transform](rawData);
    }
    return rawData;
  }, [rawData, widget]);

  const loading = propLoading ?? (widget && metricsData[widget.category] === undefined);
  const error = propError ?? null;

  if (!widget) return null;

  const renderChart = () => {
    const color = widget.color || widget.categoryColor || "#7C3AED";
    switch (widget.type) {
      case WIDGET_TYPES.LINE_CHART:
      case WIDGET_TYPES.AREA_CHART:
      case WIDGET_TYPES.SPARKLINE:
        return <Sparkline data={chartData} color={color} height={100} darkMode={darkMode} />;
      case WIDGET_TYPES.BAR_CHART:
        return <BarChart data={chartData} color={color} height={140} darkMode={darkMode} />;
      case WIDGET_TYPES.PIE_CHART:
        return <DonutChart data={chartData} size={100} donut={false} darkMode={darkMode} />;
      case WIDGET_TYPES.DONUT:
        return <DonutChart data={chartData} size={100} donut darkMode={darkMode} />;
      case WIDGET_TYPES.HEATMAP:
        return <BarChart data={chartData} color={color} height={140} darkMode={darkMode} />;
      default:
        return <Sparkline data={chartData} color={color} height={100} darkMode={darkMode} />;
    }
  };

  return (
    <BaseWidget
      id={widgetId}
      title={widget.title}
      subtitle={widget.description}
      icon={widget.icon}
      color={widget.categoryColor}
      gradient={widget.categoryGradient}
      loading={loading}
      error={error}
      showDragHandle
      isExpanded={isExpanded}
    >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        {renderChart()}
      </Box>
    </BaseWidget>
  );
}

ChartWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.shape({ message: PropTypes.string }),
  isExpanded: PropTypes.bool,
};

export default ChartWidget;
